// src/services/demoApi/damUploads.ts
// Загруженные пользователем файлы (FR-5): Blob в IndexedDB (demo_dam_uploads),
// извлечение текста из txt/md (FileReader) и docx (ленивый mammoth).
// pdf/xlsx/pptx хранятся без извлечения — extractedText = null.
import type { DamFile } from 'mocks/dam';
import { uniqueFileName, listCreatedFilesSync } from './damApi';
import { DAM_FILES } from 'mocks/dam';

const DB_NAME = 'demo_dam_uploads';
const STORE = 'files';

export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024; // 20 МБ
const ALLOWED_EXTS: Array<DamFile['ext']> = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md'];

export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

export interface UploadedDamFile {
  meta: DamFile;
  extractedText: string | null;
}

interface UploadRecord {
  id: string;
  meta: DamFile;
  extractedText: string | null;
  /** ArrayBuffer вместо Blob — совместимо со structured clone в тестах и браузере */
  blobBuffer: ArrayBuffer;
  blobType: string;
}

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
          db.close();
          reject(request.error);
        };
        transaction.onabort = () => {
          db.close();
          reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
        };
        transaction.oncomplete = () => db.close();
      })
  );
}

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/** Извлечение текста: txt/md нативно, docx — mammoth (лениво). Ошибка → null (EC-9) */
async function extractText(file: File, ext: DamFile['ext']): Promise<string | null> {
  try {
    if (ext === 'txt' || ext === 'md') return (await readAsText(file)).trim();
    if (ext === 'docx') {
      const mammoth = await import('mammoth');
      const arrayBuffer = await readAsArrayBuffer(file);
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value.trim() || null;
    }
    return null; // pdf/xlsx/pptx — не индексируем (FR-5.3)
  } catch {
    return null; // EC-9: битый файл — сохраняем без текста
  }
}

function splitName(fileName: string): { base: string; ext: DamFile['ext'] } {
  const dot = fileName.lastIndexOf('.');
  const rawExt = dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
  if (!ALLOWED_EXTS.includes(rawExt as DamFile['ext'])) {
    throw new UploadValidationError(
      `Формат «.${rawExt || '?'}» не поддерживается. Допустимы: ${ALLOWED_EXTS.map((e) => `.${e}`).join(', ')}`
    );
  }
  return { base: fileName.slice(0, dot), ext: rawExt as DamFile['ext'] };
}

export async function uploadDamFile(
  folderId: string,
  file: File,
  author: string,
  department = '—'
): Promise<DamFile> {
  if (!isIndexedDbAvailable()) {
    throw new UploadValidationError(
      'Загрузка недоступна: браузер не поддерживает локальное хранилище файлов'
    );
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    throw new UploadValidationError('Файл больше 20 МБ — выберите файл поменьше');
  }
  const { base, ext } = splitName(file.name);

  const existingNames = [
    ...DAM_FILES.filter((f) => f.folderId === folderId).map((f) => f.name),
    ...listCreatedFilesSync(folderId).map((s) => s.file.name),
    ...(await listUploadedFiles(folderId)).map((u) => u.meta.name),
  ];

  const meta: DamFile = {
    id: `upload-${Date.now()}`,
    name: uniqueFileName(base, existingNames),
    ext,
    folderId,
    sizeKb: Math.max(1, Math.round(file.size / 1024)),
    author,
    department: department.trim() || '—',
    docType: 'Загруженный',
    updatedAt: new Date().toISOString().slice(0, 10),
  };

  const extractedText = await extractText(file, ext);
  const blobBuffer = await readAsArrayBuffer(file);
  const record: UploadRecord = {
    id: meta.id,
    meta,
    blobBuffer,
    blobType: file.type,
    extractedText,
  };
  await tx('readwrite', (store) => store.put(record));
  return meta;
}

export async function listUploadedFiles(folderId?: string): Promise<UploadedDamFile[]> {
  if (!isIndexedDbAvailable()) return []; // EC-10: тихий фолбэк для листингов
  const all = await tx<UploadRecord[]>('readonly', (store) => store.getAll());
  const filtered = folderId ? all.filter((r) => r.meta.folderId === folderId) : all;
  return filtered.map(({ meta, extractedText }) => ({ meta, extractedText }));
}

export async function getUploadedBlob(id: string): Promise<Blob | null> {
  if (!isIndexedDbAvailable()) return null;
  const record = await tx<UploadRecord | undefined>('readonly', (store) => store.get(id));
  if (!record) return null;
  return new Blob([record.blobBuffer], { type: record.blobType });
}

export async function deleteUploadedFile(id: string): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  await tx('readwrite', (store) => store.delete(id));
}

/** Только для тестов */
export async function __clearUploadsForTests(): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  await tx('readwrite', (store) => store.clear());
}
